// src/pages/Case/CaseTimelinePage.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Card,
  Col,
  Row,
  Container,
  Spinner,
  Alert,
  Button,
} from "react-bootstrap";
import {
  Clock,
  GitBranch,
  FilePlus2,
  Edit3,
  CornerDownLeft,
  FileText,
  ChevronDown,
  AlertTriangle,
} from "lucide-react";

import { getCaseTimeline } from "../../services/caseService";

// CSS Module
import styles from "./TimeLine.module.css";

/**
 * Página: Timeline de un caso
 *
 * - Agrupado por fecha (Hoy, Ayer, etc.)
 * - Cada grupo de fecha se puede colapsar/expandir
 * - Soporta:
 *    - CASE_CREATED
 *    - STATUS_CHANGED
 *    - CASE_UPDATED
 *    - CASE_NOTE_ADDED
 */
const CaseTimelinePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Ruta por defecto en caso de no venir de lista
  const fallbackPath = "/dashboard/cases";

  // Si venimos de my-cases o de cases, viene aquí
  const from = location.state?.from || fallbackPath;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set()); //control de acordeón

  // ---------------------------------------------------
  // Cargar timeline desde el backend
  // ---------------------------------------------------
  useEffect(() => {
    let alive = true;

    const loadData = async () => {
      setLoading(true);
      setLoadError(null);

      try {
        const resp = await getCaseTimeline(id);
        if (!resp?.success) {
          throw new Error(resp?.message || "No se pudo cargar el timeline.");
        }

        const sorted = [...(resp.data || [])].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        if (alive) setItems(sorted);
      } catch (err) {
        console.error("Error cargando timeline del caso:", err);
        if (alive) {
          setLoadError(
            err?.message || "Ocurrió un error al cargar el timeline del caso."
          );
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    if (id) loadData();

    return () => {
      alive = false;
    };
  }, [id]);

  // ---------------------------------------------------
  // Helpers de fecha / agrupación
  // ---------------------------------------------------

  const formatDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("es-CO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const toDateKey = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "unknown";
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const isSameDay = (d1, d2) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const formatDateGroupLabel = (date) => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (isSameDay(date, today)) return "Hoy";
    if (isSameDay(date, yesterday)) return "Ayer";

    return date.toLocaleDateString("es-CO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const groupByDate = (events) => {
    const map = new Map();

    events.forEach((ev) => {
      const d = new Date(ev.createdAt);
      if (Number.isNaN(d.getTime())) return;

      const key = toDateKey(ev.createdAt);
      if (!map.has(key)) {
        map.set(key, { date: d, items: [] });
      }
      map.get(key).items.push(ev);
    });

    const groups = Array.from(map.entries())
      .sort((a, b) => b[1].date - a[1].date)
      .map(([key, value]) => ({
        key,
        date: value.date,
        label: formatDateGroupLabel(value.date),
        items: value.items,
      }));

    return groups;
  };

  const grouped = useMemo(() => groupByDate(items), [items]);

  // Inicializar acordeón (por defecto todas abiertas cuando cambia la data)
  useEffect(() => {
    const allKeys = new Set(grouped.map((g) => g.key));
    setExpandedGroups(allKeys);
  }, [grouped]);

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ---------------------------------------------------
  // Otros helpers de UI
  // ---------------------------------------------------

  const getIcon = (eventType) => {
    switch (eventType) {
      case "CASE_CREATED":
        return <FilePlus2 size={18} />;
      case "STATUS_CHANGED":
        return <GitBranch size={18} />;
      case "CASE_UPDATED":
        return <Edit3 size={18} />;
      case "CASE_NOTE_ADDED":
        return <FileText size={18} />;
      case "CASE_NOTE_DELETED":
        return <FileText size={18} style={{ opacity: 0.5 }} />;
      case "CASE_NOTE_ATTACHMENT_DELETED":
        return <FileText size={18} style={{ opacity: 0.6 }} />;
      case "CASE_EXPIRED_AUTO":
        return <AlertTriangle size={18} />;
      case "CASE_WARNING_SENT":
        return <AlertTriangle size={18} />;
      case "PRIORITY_AUTO_UPDATED":
        return <AlertTriangle size={18} />;
      default:
        return <Clock size={18} />;
    }
  };

  const getEventTypeLabel = (eventType) => {
    switch (eventType) {
      case "CASE_CREATED":
        return "Caso creado";
      case "STATUS_CHANGED":
        return "Cambio de estado";
      case "CASE_UPDATED":
        return "Caso actualizado";
      case "CASE_NOTE_ADDED":
        return "Nota registrada";
      case "CASE_NOTE_DELETED":
        return "Nota eliminada";
      case "CASE_NOTE_ATTACHMENT_DELETED":
        return "Adjunto de nota eliminado";
      case "CASE_EXPIRED_AUTO":
        return "Vencido automáticamente";
      case "CASE_WARNING_SENT":
        return "Advertencia enviada";
      case "PRIORITY_AUTO_UPDATED":
        return "Prioridad actualizada automáticamente";
      default:
        return eventType || "Evento";
    }
  };

  const getChanges = (metadataJson) => {
    if (!metadataJson) return [];

    try {
      const meta = JSON.parse(metadataJson);
      const raw = meta.Changes || meta.changes || [];
      if (!Array.isArray(raw)) return [];

      return raw.map((c) => ({
        label: c.DisplayName || c.displayName || c.Field || c.field || "Campo",
        from: c.FromDisplay ?? c.fromDisplay ?? c.From ?? c.from ?? null,
        to: c.ToDisplay ?? c.toDisplay ?? c.To ?? c.to ?? null,
      }));
    } catch {
      return [];
    }
  };

  const getNoteMetadata = (metadataJson) => {
    if (!metadataJson) return null;

    try {
      const meta = JSON.parse(metadataJson);
      return {
        noteId: meta.noteId || null,
        noteTitle: meta.noteTitle || null,
        noteIsInternal:
          typeof meta.noteIsInternal === "boolean"
            ? meta.noteIsInternal
            : false,
        attachmentsCount:
          typeof meta.attachmentsCount === "number"
            ? meta.attachmentsCount
            : null,
      };
    } catch {
      return null;
    }
  };

  const getWarningMetadata = (metadataJson) => {
    if (!metadataJson) return null;

    try {
      const meta = JSON.parse(metadataJson);
      return {
        deadline: meta.deadline || null,
        daysRemaining:
          typeof meta.daysRemaining === "number" ? meta.daysRemaining : null,
        alertDaysBefore:
          typeof meta.alertDaysBefore === "number"
            ? meta.alertDaysBefore
            : null,
      };
    } catch {
      return null;
    }
  };

  const formatBooleanForField = (label, value) => {
    const name = (label || "").toLowerCase();
    if (name.includes("activo")) {
      return value ? "Activo" : "Inactivo";
    }
    return value ? "Verdadero" : "Falso";
  };

  const formatChangeValue = (label, value) => {
    if (value === null || value === undefined) return "—";
    if (typeof value === "boolean") {
      return formatBooleanForField(label, value);
    }
    if (typeof value === "string" && value.length === 0) return "—";
    return value;
  };

  // ---------------------------------------------------
  // Render
  // ---------------------------------------------------
  return (
    <Container className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-1">Timeline del caso</h3>
          <p className="text-muted small mb-0">
            Historial completo de eventos, cambios y notas registradas sobre
            este caso.
          </p>
        </div>

        <Button
          variant="secondary"
          onClick={() => navigate(from)}
          className="d-flex align-items-center gap-1"
        >
          <CornerDownLeft size={16} />
          Volver a casos
        </Button>
      </div>

      {/* Estados de carga / error */}
      {loading && (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando timeline...</span>
          </Spinner>
        </div>
      )}

      {!loading && loadError && <Alert variant="danger">{loadError}</Alert>}

      {!loading && !loadError && items.length === 0 && (
        <Alert variant="info">
          Aún no hay eventos registrados en el historial de este caso.
        </Alert>
      )}

      {!loading && !loadError && items.length > 0 && (
        <Row>
          <Col lg={12}>
            <Card className="shadow-sm">
              <Card.Header className="border-bottom-0">
                <h6 className="mb-0 text-dark">Historial de eventos</h6>
              </Card.Header>

              <Card.Body>
                <div className={styles.timelineContainer}>
                  {grouped.map((group) => {
                    const isExpanded = expandedGroups.has(group.key);
                    const eventCount = group.items.length;

                    return (
                      <div key={group.key} className={styles.timelineDateGroup}>
                        {/* Cabecera de grupo por fecha (colapsable) */}
                        <button
                          type="button"
                          className={styles.timelineDateHeaderButton}
                          onClick={() => toggleGroup(group.key)}
                        >
                          <div className={styles.timelineDateHeaderLeft}>
                            <span
                              className={`${styles.timelineChevron} ${
                                !isExpanded
                                  ? styles.timelineChevronCollapsed
                                  : ""
                              }`}
                            >
                              <ChevronDown size={14} />
                            </span>
                            <span className={styles.timelineDatePill}>
                              {group.label}
                            </span>
                          </div>
                          <div className={styles.timelineDateHeaderRight}>
                            <span className={styles.timelineDateCounter}>
                              {eventCount} evento
                              {eventCount !== 1 && "s"}
                            </span>
                          </div>
                        </button>

                        {/* Eventos de esa fecha (solo si está expandido) */}
                        {isExpanded &&
                          group.items.map((item) => {
                            const changes = getChanges(item.metadataJson);

                            const isCreated = item.eventType === "CASE_CREATED";
                            const isStatusChanged =
                              item.eventType === "STATUS_CHANGED";
                            const isNoteAdded =
                              item.eventType === "CASE_NOTE_ADDED";
                            const isNoteDeleted =
                              item.eventType === "CASE_NOTE_DELETED";
                            const isNoteAttachmentDeleted =
                              item.eventType === "CASE_NOTE_ATTACHMENT_DELETED";
                            const isAutoExpired =
                              item.eventType === "CASE_EXPIRED_AUTO";
                            const isWarningSent =
                              item.eventType === "CASE_WARNING_SENT";
                            const isPriorityUpdated =
                              item.eventType === "PRIORITY_AUTO_UPDATED";

                            const noteMeta =
                              isNoteAdded ||
                              isNoteDeleted ||
                              isNoteAttachmentDeleted
                                ? getNoteMetadata(item.metadataJson)
                                : null;

                            const warningMeta = isWarningSent
                              ? getWarningMetadata(item.metadataJson)
                              : null;

                            return (
                              <div
                                key={item.id}
                                className={styles.timelineItem}
                              >
                                {/* Línea vertical */}
                                <div className={styles.timelineLine} />

                                {/* Icono circular */}
                                <div
                                  className={`${styles.timelineIconWrapper} ${
                                    isCreated
                                      ? styles.iconCreated
                                      : isStatusChanged
                                      ? styles.iconStatusChanged
                                      : isNoteAdded
                                      ? styles.iconNote
                                      : isNoteDeleted
                                      ? styles.iconNoteDeleted
                                      : isNoteAttachmentDeleted
                                      ? styles.iconAttachmentDeleted
                                      : isAutoExpired
                                      ? styles.iconAutoExpired
                                      : isWarningSent
                                      ? styles.iconWarningSent
                                      : isPriorityUpdated
                                      ? styles.iconPriorityUpdated
                                      : ""
                                  }`}
                                >
                                  {getIcon(item.eventType)}
                                </div>

                                {/* Card del evento */}
                                <div
                                  className={`${styles.timelineCard} ${
                                    isCreated
                                      ? styles.cardCreated
                                      : isStatusChanged
                                      ? styles.cardStatusChanged
                                      : isNoteAdded
                                      ? styles.cardNote
                                      : isNoteDeleted
                                      ? styles.cardNoteDeleted
                                      : isNoteAttachmentDeleted
                                      ? styles.cardNoteDeleted
                                      : isAutoExpired
                                      ? styles.cardAutoExpired
                                      : isWarningSent
                                      ? styles.cardWarningSent
                                      : isPriorityUpdated
                                      ? styles.cardPriorityUpdated
                                      : ""
                                  }`}
                                >
                                  <div className="d-flex justify-content-between align-items-start mb-1">
                                    <h6 className={styles.timelineTitle}>
                                      {item.title}
                                    </h6>
                                    <span
                                      className={`${styles.timelineEventType} ${
                                        isCreated
                                          ? styles.badgeCreated
                                          : isStatusChanged
                                          ? styles.badgeStatusChanged
                                          : isNoteAdded
                                          ? styles.badgeNote
                                          : isNoteDeleted
                                          ? styles.badgeNoteDeleted
                                          : isNoteAttachmentDeleted
                                          ? styles.badgeNoteDeleted
                                          : isAutoExpired
                                          ? styles.badgeAutoExpired
                                          : isWarningSent
                                          ? styles.badgeWarningSent
                                          : isPriorityUpdated
                                          ? styles.badgePriorityUpdated
                                          : ""
                                      }`}
                                    >
                                      {getEventTypeLabel(item.eventType)}
                                    </span>
                                  </div>

                                  {item.description && (
                                    <p className={styles.timelineDescription}>
                                      {item.description}
                                    </p>
                                  )}

                                  {/* Bloque especial para alerta de vencimiento enviada */}
                                  {isWarningSent && warningMeta && (
                                    <div className={styles.warningMetaBlock}>
                                      <div className="small text-muted">
                                        <strong>Alerta de vencimiento:</strong>{" "}
                                        {typeof warningMeta.daysRemaining ===
                                          "number" && (
                                          <>
                                            Faltan{" "}
                                            <strong>
                                              {warningMeta.daysRemaining}
                                            </strong>{" "}
                                            día
                                            {warningMeta.daysRemaining !== 1 &&
                                              "s"}{" "}
                                            para la fecha límite.
                                          </>
                                        )}
                                        {warningMeta.deadline && (
                                          <>
                                            {" "}
                                            Fecha límite:{" "}
                                            <strong>
                                              {warningMeta.deadline}
                                            </strong>
                                            .
                                          </>
                                        )}
                                        {typeof warningMeta.alertDaysBefore ===
                                          "number" && (
                                          <>
                                            {" "}
                                            (Configurada{" "}
                                            {warningMeta.alertDaysBefore} día
                                            {warningMeta.alertDaysBefore !==
                                              1 && "s"}{" "}
                                            antes)
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Bloque especial para notas */}
                                  {isNoteAdded && noteMeta && (
                                    <div className={styles.noteMetaBlock}>
                                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                        <span className={styles.noteTitle}>
                                          Nota:{" "}
                                          <strong>
                                            {noteMeta.noteTitle || "Sin título"}
                                          </strong>
                                        </span>
                                        <span
                                          className={`${
                                            styles.noteVisibilityPill
                                          } ${
                                            noteMeta.noteIsInternal
                                              ? styles.noteInternal
                                              : styles.noteExternal
                                          }`}
                                        >
                                          {noteMeta.noteIsInternal
                                            ? "Solo uso interno"
                                            : "Visible para el cliente"}
                                        </span>
                                      </div>

                                      {typeof noteMeta.attachmentsCount ===
                                        "number" &&
                                        noteMeta.attachmentsCount > 0 && (
                                          <div
                                            className={
                                              styles.noteAttachmentsInfo
                                            }
                                          >
                                            {noteMeta.attachmentsCount} archivo
                                            {noteMeta.attachmentsCount !== 1 &&
                                              "s"}{" "}
                                            adjunto
                                            {noteMeta.attachmentsCount > 1 &&
                                              "s"}
                                          </div>
                                        )}
                                    </div>
                                  )}

                                  {isNoteDeleted && noteMeta && (
                                    <div className={styles.noteMetaBlock}>
                                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                        <span className={styles.noteTitle}>
                                          Nota eliminada:{" "}
                                          <strong>
                                            {noteMeta.noteTitle || "Sin título"}
                                          </strong>
                                        </span>
                                        <span
                                          className={`${
                                            styles.noteVisibilityPill
                                          } ${
                                            noteMeta.noteIsInternal
                                              ? styles.noteInternal
                                              : styles.noteExternal
                                          }`}
                                        >
                                          {noteMeta.noteIsInternal
                                            ? "Solo uso interno"
                                            : "Visible para el cliente"}
                                        </span>
                                      </div>

                                      {typeof noteMeta.attachmentsCount ===
                                        "number" &&
                                        noteMeta.attachmentsCount > 0 && (
                                          <div
                                            className={
                                              styles.noteAttachmentsInfo
                                            }
                                          >
                                            {noteMeta.attachmentsCount} archivo
                                            {noteMeta.attachmentsCount !== 1 &&
                                              "s"}{" "}
                                            que fueron eliminados junto con la
                                            nota
                                          </div>
                                        )}
                                    </div>
                                  )}

                                  {isNoteAttachmentDeleted && noteMeta && (
                                    <div className={styles.noteMetaBlock}>
                                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                        <span className={styles.noteTitle}>
                                          Adjunto eliminado de la nota:{" "}
                                          <strong>
                                            {noteMeta.noteTitle || "Sin título"}
                                          </strong>
                                        </span>
                                        <span
                                          className={`${
                                            styles.noteVisibilityPill
                                          } ${
                                            noteMeta.noteIsInternal
                                              ? styles.noteInternal
                                              : styles.noteExternal
                                          }`}
                                        >
                                          {noteMeta.noteIsInternal
                                            ? "Nota de uso interno"
                                            : "Nota visible para el cliente"}
                                        </span>
                                      </div>

                                      {typeof noteMeta.attachmentsCount ===
                                        "number" &&
                                        noteMeta.attachmentsCount > 0 && (
                                          <div
                                            className={
                                              styles.noteAttachmentsInfo
                                            }
                                          >
                                            {noteMeta.attachmentsCount} archivo
                                            {noteMeta.attachmentsCount !== 1 &&
                                              "s"}{" "}
                                            adjunto
                                            {noteMeta.attachmentsCount > 1 &&
                                              "s"}{" "}
                                            eliminado
                                            {noteMeta.attachmentsCount > 1 &&
                                              "s"}
                                            .
                                          </div>
                                        )}
                                    </div>
                                  )}

                                  {/* Cambio de estado explícito */}
                                  {item.fromStatus || item.toStatus ? (
                                    <p className="small mb-2">
                                      <strong>Estado:&nbsp;</strong>
                                      <span className="text-muted">
                                        {item.fromStatus || "—"} {" → "}
                                        {item.toStatus || "—"}
                                      </span>
                                    </p>
                                  ) : null}

                                  {/* Cambios de campos */}
                                  {changes.length > 0 && (
                                    <div
                                      className={styles.timelineChangesBlock}
                                    >
                                      <strong className="d-block mb-1">
                                        Cambios registrados:
                                      </strong>
                                      <ul
                                        className={styles.timelineChangesList}
                                      >
                                        {changes.map((c, i) => (
                                          <li
                                            key={i}
                                            className={
                                              styles.timelineChangeItem
                                            }
                                          >
                                            <span
                                              className={
                                                styles.timelineChangeLabel
                                              }
                                            >
                                              {c.label}:
                                            </span>{" "}
                                            <span className="text-muted">
                                              {formatChangeValue(
                                                c.label,
                                                c.from
                                              )}{" "}
                                              {" → "}
                                              {formatChangeValue(c.label, c.to)}
                                            </span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Pie de card */}
                                  <div className={styles.timelineFooter}>
                                    <Clock size={13} className="me-1" />
                                    <span>
                                      {formatDateTime(item.createdAt)}.
                                    </span>
                                    {item.performedByUserName && (
                                      <span className="ms-3">
                                        Usuario:&nbsp;
                                        <strong>
                                          {item.performedByUserName}
                                        </strong>
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    );
                  })}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default CaseTimelinePage;
